// Inventory-category → P&L-category assignment logic.
//
// This module backs the row-based assignment table that replaced the old
// connector-line mapping UI. It is intentionally free of React and Supabase
// coupling for the pure functions (suggestion + suspicious-mapping detection),
// so they can be unit-tested in isolation; the small persistence helpers at the
// bottom take a `supabase` client as an argument.
//
// Status model (derived, not separately persisted):
//   - confirmed  → a saved mapping exists (pnl_category_mappings /
//                  clover_category_mappings). Legacy mappings load as confirmed.
//   - suggested  → no saved mapping, but an automatic suggestion was computed.
//   - review     → an assigned/suggested mapping looks unusual (suspicious).
//   - excluded   → explicitly excluded from P&L reporting.
//   - unassigned → none of the above.
//
// Because "confirmed" == "has a saved mapping", a confirmed user mapping is
// never replaced by a new automatic suggestion (suggestions are only computed
// for categories that have no saved mapping). This satisfies the data-model
// intent (assignment_status / suggestion_method / confirmed_by_user …) without
// a schema migration, so existing P&L category IDs, source-category IDs,
// exclusions, and historical reports are all preserved.

export const STATUS = {
  SUGGESTED: 'suggested',
  CONFIRMED: 'confirmed',
  REVIEW: 'review',
  EXCLUDED: 'excluded',
  UNASSIGNED: 'unassigned',
};

export const STATUS_META = {
  [STATUS.CONFIRMED]: { label: 'Confirmed', icon: '✓', color: '#1a5c34', bg: '#E4F6EA', border: '#A9DCBC' },
  [STATUS.SUGGESTED]: { label: 'Suggested', icon: '✨', color: '#7B5E45', bg: '#FBF3E8', border: '#E6CFA8' },
  [STATUS.REVIEW]: { label: 'Review', icon: '⚠', color: '#8a6d1b', bg: '#FFF8E1', border: '#F0D78A' },
  [STATUS.EXCLUDED]: { label: 'Excluded', icon: '⊘', color: '#6b5b53', bg: '#F0EAE4', border: '#D8CABE' },
  [STATUS.UNASSIGNED]: { label: 'Unassigned', icon: '•', color: '#8B7B73', bg: '#F7F2EC', border: '#E8DDD0' },
};

// ── Name normalization ────────────────────────────────────────────────────
export function normalizeName(s) {
  return (s || '')
    .toString()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

// Light singularization so "Beverages" matches "Beverage" etc.
function singular(norm) {
  return norm
    .split(' ')
    .map((w) => (w.length > 3 && w.endsWith('s') ? w.replace(/s$/, '') : w))
    .join(' ');
}

function normEq(a, b) {
  const na = normalizeName(a);
  const nb = normalizeName(b);
  return na === nb || singular(na) === singular(nb);
}

// ── Synonyms (source name → canonical P&L name) ───────────────────────────
// Resolved against the store's actual P&L categories; if the canonical target
// does not exist as a category the rule is skipped and we fall through to the
// family heuristic.
const SYNONYMS = {
  'na beverage': 'non alcoholic beverages',
  'na beverages': 'non alcoholic beverages',
  'n a beverage': 'non alcoholic beverages',
  'non alcoholic beverage': 'non alcoholic beverages',
  'non alc beverage': 'non alcoholic beverages',
  spirits: 'liquor',
  spirit: 'liquor',
  alcohol: 'liquor',
  'well liquor': 'liquor',
  'draft beer': 'beer',
  'draught beer': 'beer',
  'bottled beer': 'beer',
  'wine by the glass': 'wine',
  'wine by glass': 'wine',
  'bar supplies': 'bar consumables',
  'bar consumable': 'bar consumables',
  produce: 'food',
  meat: 'food',
  'dry goods': 'food',
  'dry good': 'food',
  dairy: 'food',
  seafood: 'food',
  bakery: 'food',
  poultry: 'food',
};

// ── Category "families" for the fuzzy fallback + suspicious detection ──────
const FAMILY_KEYWORDS = {
  food: ['food', 'produce', 'meat', 'poultry', 'seafood', 'fish', 'dairy', 'bakery', 'bread', 'dry good', 'grocery', 'pantry', 'vegetable', 'fruit', 'deli', 'prepared'],
  beer: ['beer', 'draft', 'draught', 'ale', 'lager', 'ipa', 'cider'],
  wine: ['wine', 'vino', 'sparkling', 'champagne'],
  liquor: ['liquor', 'spirit', 'whiskey', 'whisky', 'vodka', 'gin', 'rum', 'tequila', 'bourbon', 'scotch', 'cocktail', 'mixer', 'alcohol'],
  retail: ['retail', 'merch', 'merchandise', 'apparel', 'clothing', 'souvenir'],
  supplies: ['supply', 'supplies', 'consumable', 'paper', 'packaging', 'disposable', 'container', 'napkin', 'utensil', 'cleaning', 'smallware'],
  na_bev: ['soda', 'juice', 'coffee', 'tea', 'water', 'soft drink', 'beverage', 'na bev'],
};

const FAMILY_ORDER = ['food', 'beer', 'wine', 'liquor', 'retail', 'supplies', 'na_bev'];

export function familyOf(name) {
  const n = normalizeName(name);
  if (!n) return null;
  // Non-alcoholic must win over the "alcohol" keyword in the liquor family.
  if (/\bnon alcoholic\b/.test(n) || /\bnon alc\b/.test(n) || /^na /.test(n)) return 'na_bev';
  for (const fam of FAMILY_ORDER) {
    if (FAMILY_KEYWORDS[fam].some((kw) => n.includes(kw))) return fam;
  }
  return null;
}

// ── Non-cost source categories (tax, tips, gift cards…) ───────────────────
const NON_COST_KEYWORDS = [
  'tax', 'tip', 'gratuity', 'gift card', 'giftcard', 'deposit', 'service charge',
  'service fee', 'surcharge', 'refund', 'comp', 'void', 'rounding', 'donation',
  'no sale', 'open item', 'open food', 'open bar', 'misc payment', 'store credit',
];

function hasKeyword(name, kw) {
  const n = normalizeName(name);
  if (kw.includes(' ')) return n.includes(kw);
  // Single-word keywords match whole tokens (so "taxable" ≠ "tax"), allowing
  // for a simple plural ("Tips" → "tip").
  return n.split(' ').some((tok) => tok === kw || singular(tok) === kw);
}

export function isNonCostCategory(name) {
  return NON_COST_KEYWORDS.some((kw) => hasKeyword(name, kw));
}

/**
 * Compute an automatic suggestion for a source category that has no saved
 * mapping. Priority (per spec): exact normalized match → known synonym →
 * fuzzy family match. Returns { pnlId, pnlName, method, confidence } or null.
 */
export function suggestPnlCategory(sourceName, pnlCats = []) {
  if (!sourceName || !pnlCats.length) return null;
  const norm = normalizeName(sourceName);

  // 1. Exact normalized name match.
  const exact = pnlCats.find((c) => normEq(c.name, sourceName));
  if (exact) return { pnlId: exact.id, pnlName: exact.name, method: 'exact', confidence: 'high' };

  // 2. Known synonym → canonical target category.
  const canonical = SYNONYMS[norm] || SYNONYMS[singular(norm)];
  if (canonical) {
    const target = pnlCats.find((c) => normEq(c.name, canonical));
    if (target) return { pnlId: target.id, pnlName: target.name, method: 'synonym', confidence: 'high' };
  }

  // 3. Fuzzy family match (heuristic).
  const fam = familyOf(sourceName);
  if (fam) {
    const target = pnlCats.find((c) => familyOf(c.name) === fam);
    if (target) return { pnlId: target.id, pnlName: target.name, method: 'heuristic', confidence: 'medium' };
  }

  return null;
}

/**
 * Flag a mapping that looks unusual based on the category names, without
 * blocking the user. Returns { message } or null.
 *
 *  - A source category that looks non-cost (tax, tips, gift cards…) assigned to
 *    a real cost category.
 *  - A strong name signal (exact/synonym suggestion) that disagrees with the
 *    chosen P&L category (e.g. inventory "Food" assigned to "App").
 *  - Two clearly different families (e.g. "Beer" assigned to "Retail").
 */
export function detectSuspiciousAssignment(sourceName, pnlName, pnlCats = []) {
  if (!sourceName || !pnlName) return null;

  if (isNonCostCategory(sourceName) && !isNonCostCategory(pnlName)) {
    return {
      message: `“${sourceName}” looks like a non-cost category (tax, tips, gift cards, deposits…). Confirm it should report under “${pnlName}”, or exclude it from P&L reporting.`,
    };
  }

  // Strong name signal that disagrees with the selection.
  const strong = suggestPnlCategory(sourceName, pnlCats);
  if (strong && (strong.method === 'exact' || strong.method === 'synonym') && !normEq(strong.pnlName, pnlName)) {
    return {
      message: `Based on its name, “${sourceName}” usually reports under “${strong.pnlName}”. Confirm that assigning it to “${pnlName}” is intentional.`,
    };
  }

  const sf = familyOf(sourceName);
  const pf = familyOf(pnlName);
  if (sf && pf && sf !== pf) {
    return {
      message: `“${sourceName}” and “${pnlName}” look like different categories. Confirm that this assignment is intentional.`,
    };
  }

  return null;
}

/**
 * Derive the display status for one assignment row.
 *   row: { pnlId|null, excluded, suggestion|null }
 *   pnlName: resolved name for pnlId (when assigned)
 */
export function deriveStatus(row, pnlName, pnlCats = []) {
  if (row.excluded) return STATUS.EXCLUDED;
  if (row.pnlId != null) {
    return detectSuspiciousAssignment(row.name, pnlName, pnlCats) ? STATUS.REVIEW : STATUS.CONFIRMED;
  }
  if (row.suggestion) {
    return detectSuspiciousAssignment(row.name, row.suggestion.pnlName, pnlCats) ? STATUS.REVIEW : STATUS.SUGGESTED;
  }
  return STATUS.UNASSIGNED;
}

// ── Persistence helpers (shared by the table, the panel, and the wizard) ──
// These mirror the exact writes the previous UI performed, so the underlying
// rows, IDs, and the resolver behavior are unchanged.

export async function setInventoryMapping(supabase, storeId, inventoryCategory, pnlId) {
  await supabase
    .from('pnl_category_mappings')
    .delete()
    .eq('store_id', storeId)
    .eq('inventory_category', inventoryCategory);
  if (pnlId != null) {
    const { error } = await supabase
      .from('pnl_category_mappings')
      .insert({ store_id: storeId, pnl_category_id: pnlId, inventory_category: inventoryCategory });
    if (error) throw error;
  }
}

export async function setCloverMapping(supabase, storeId, cat, pnlId) {
  // cat: { id, name, type }
  if (pnlId == null) {
    await supabase
      .from('clover_category_mappings')
      .delete()
      .eq('store_id', storeId)
      .eq('category_type', cat.type)
      .eq('clover_category_id', cat.id);
    return;
  }
  const { error } = await supabase.from('clover_category_mappings').upsert(
    {
      store_id: storeId,
      clover_category_id: cat.id,
      clover_category_name: cat.name,
      category_type: cat.type,
      pnl_category_id: Number(pnlId),
      mapping_source: 'manual',
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'store_id,clover_category_id' },
  );
  if (error) throw error;
}
